import { useEffect, useState, useMemo } from 'react';
import set from 'lodash/set';
import find from 'lodash/find';

import { assertHasProperty } from 'common/assertions';
import { type Vif } from 'common/visualizations/vif';
import HTTPError from 'common/errors/HTTPError';
import MetadataProvider from 'common/visualizations/dataProviders/MetadataProvider';
import { type View } from 'common/types/view';

import { type VizCanvasDatasetConfig } from 'types';
import StoryVisualization from 'lib/components/StoryVisualization';

export interface VizCanvasProps {
  visualizationConfig: VizCanvasDatasetConfig;
  useMetadataCache?: boolean;
  /** Must be referentially stable — it is a dependency of the vif-fetching effect. */
  onError: (hasError: boolean) => void;
}

const findMatchingVifInView = (view: View, vifId: string): Vif => {
  assertHasProperty(view, 'displayFormat.visualizationCanvasMetadata.vifs');
  const vifs = view.displayFormat?.visualizationCanvasMetadata?.vifs;
  const newVif = find(vifs, { id: vifId }) as Vif;

  // Core's serialization strips out any properties that have null or "empty"
  // values. The vif has properties whose values are intentionally null, not including
  // them will prevent the visualization from rendering. We handle this for embeds
  // and Viz Cans in frontend/app/models/display_format.rb#restore_required_vif_metadata
  const measure = newVif.series[0].dataSource.measure;
  if (measure) {
    measure.aggregationFunction ||= null;
  }

  return newVif;
};

const getVif = async (config: VizCanvasDatasetConfig, metadataProvider: MetadataProvider): Promise<Vif> => {
  const { datasetUid, vifId } = config;
  const datasetMetadataAndFederationStatus = await metadataProvider.getDatasetMetadataAndFederationStatus();
  const view = datasetMetadataAndFederationStatus.metadata;
  const newVif = findMatchingVifInView(view, vifId);
  if (!newVif) {
    throw new HTTPError(404, `Could not find VIF with id ${vifId} in the view.`);
  }

  // Remove any domain information from the vif's series data sources, we already do this for viz cans
  newVif.series.forEach((series) => {
    if (series.dataSource?.domain) {
      delete series.dataSource.domain;
    }
  });

  // Set vif.origin.url to point to the vizcan asset on the appropriate domain
  const attributionDomain = await metadataProvider.getAttributionDomain(datasetMetadataAndFederationStatus);
  if (attributionDomain) {
    set(newVif, 'origin.url', `https://${attributionDomain}/d/${datasetUid}`);
    set(newVif, 'origin.title', view.name);
  }

  return newVif;
};

/**
 * Resolves the vif referenced by a visualization canvas asset and renders it. Reports
 * resolution failures through `onError` so the caller can render its own error state.
 */
export const VizCanvas = ({ visualizationConfig, useMetadataCache = true, onError }: VizCanvasProps) => {
  const [vif, setVif] = useState<Vif | null>(null);
  const metadataProvider = useMemo(
    () => new MetadataProvider({ datasetUid: visualizationConfig.datasetUid }, useMetadataCache),
    [visualizationConfig, useMetadataCache],
  );

  useEffect(() => {
    getVif(visualizationConfig, metadataProvider)
      .then((newVif: Vif) => {
        onError(false);
        setVif(newVif);
      })
      .catch(() => {
        onError(true);
      });
  }, [visualizationConfig, metadataProvider, onError]);

  if (!vif) {
    return null;
  }

  const additionalOptions = { displayFilterBar: true, toggleMapLayersInternally: true };

  return <StoryVisualization storyVif={vif} options={additionalOptions} />;
};
